import { Box } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import { labelids } from "../../assets/data/TkExplanationProperty";
import TkButton from "../../components/atoms/TkButton";
import TkStepper from "../../components/atoms/TkStepper";
import TkTable from "../../components/atoms/TkTable";
import TkBackButton from "../../components/molecules/TkBackButton";
import TkContractorAndNumberLine from "../../components/molecules/TkContractorAndNumberLine";
import TkDateDialog from "../../components/molecules/TkDateDialog";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";
import { BusinessContext } from "../../context/businessContext";
import { GlobalContext } from "../../context/globalContext";
import { IAPI_PRE006_Request } from "../../type/apiType";
import { changeAmountFormat } from "../../utils/useTools";
import "./_style.scss";
import { httpAPI_PRE006, httpAPI_PRE007, httpAPI_PRE008 } from "./api/request";
import { stepperData, tableHeadList as initialTableHeadList } from "./parts";
import TkHelpIcon from "../../components/atoms/TkHelpIcon";
import { Status } from "../../assets/data/Constants";

/**
 * SCRC080006 掛金情報確認 施設一覧
 * @return SCRC080006
 */
const SCRC080006 = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state: businessState } = useContext(BusinessContext);
  const { state, setState } = useContext(GlobalContext);
  const [tableHeadList, setTableHeadList] = useState(initialTableHeadList);
  const [premiumFacilityBodyList, setPremiumFacilityBodyList] = useState<any>(
    []
  );
  const [submitDataList, setSubmitDataList] = useState<IAPI_PRE006_Request>();

  // 掛金額の合計
  const [premiumSum, setPremiumSum] = useState<number>(0);
  const [isOpen, setIsOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(2);
  const [isBtnVisible, setIsBtnVisible] = useState(true);

  const userType = state?.authInfo?.userType;
  // ユーザタイプが機構
  const isKiko = userType === authorityUserType.userTypeNumber.KIKO;
  const contractorNo = isKiko
    ? businessState.common?.contractorNo
    : state?.authInfo?.contractorNo;
  const applyId = location?.state?.applyId! ?? businessState.job_11?.applyId;

  // 初期化
  const handleInit = async () => {
    try {
      // 掛金納付対象職員届参照API呼び出し
      const res = applyId
        ? await httpAPI_PRE008({
            applyId: applyId,
          })
        : ((await httpAPI_PRE007({
            contractorNo: contractorNo,
          })) as any);

      if (res.resultCd === "I0000") {
        if (applyId) {
          if (
            res.latestApplyStatus === Status["届出待ち"] ||
            res.latestApplyStatus === Status["差戻し一時保存"]
          ) {
            setActiveStep(2);
            setIsBtnVisible(true);
          } else {
            setActiveStep(4);
            setIsBtnVisible(false);
          }
        }
        // レスポンスの値からコンポーネント形に変更
        const premiumFacilityBodyList = funPremiumFacilityBodyList(
          res.facilityList
        );
        setPremiumFacilityBodyList(premiumFacilityBodyList);

        // 提出データの作成
        let temp: IAPI_PRE006_Request = {
          contractorNo: contractorNo,
          year: res.year,
          facilityList: res.facilityList?.map((item: any) => {
            return {
              facilityNo: item.facilityNo,
              premiumAmt: item.amt,
              obsoleteDate: item.obsoleteDate,
              maStaffNo: item.premiumPaymentStaffNum ?? item.premiumStaffNum,
              unitPremiumStaffNum: item.unitPremiumStaffNum,
              triplePremiumStaffNum: item.triplePremiumStaffNum,
            };
          }),
          kyoSubmissionAgreeDate: res.kyoSubmissionAgreeDate,
          updateDateTime: res.updateDateTime,
          screenId: "SCRC080006",
        };

        setSubmitDataList(temp);
        setState({
          ...state,
          errorMsgId: "",
        });
        const updatedTableHeadList = initialTableHeadList.map((head: any) => {
          if (head.className === "unitCreditCost") {
            return {
              ...head,
              headTitle: (
                <>
                  単位掛金額対象職員数(単位金額：{res.unitPremiumAmt ?? 0}円)
                  <TkHelpIcon
                    helpMessage={
                      <>
                        施設ごとに単位掛金額の対象者数を表示しています。社会福祉施設等に在籍する職員数はすべての職員が単位掛金額の対象となります。特定介護保険施設等に在籍する職員数は制度改正前に加入された職員数です。
                      </>
                    }
                  />
                </>
              ),
            };
          }
          if (head.className === "unitCreditCostThreeTimes") {
            return {
              ...head,
              headTitle: (
                <>
                  単位掛金額×3対象職員数(単位金額×3：
                  {(res.unitPremiumAmt ?? 0) * 3}円)
                  <TkHelpIcon
                    helpMessage={
                      <>
                        施設ごとに「単位掛金額×３」の対象者数を表示しています。申出施設等に在籍する職員数はすべての職員が「単位掛金額×３」の対象となります。特定介護保険施設等に在籍する職員数は制度改正後に加入された職員数です。
                      </>
                    }
                  />
                </>
              ),
            };
          }
          return head;
        });
        setTableHeadList(updatedTableHeadList);
      } else {
        setState({
          ...state,
          errorMsgId:
            (res as any).resultMsg || (res as any).message || res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  // 初期表示
  useEffect(() => {
    handleInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function funPremiumFacilityBodyList(facilityList: any) {
    let premiumSumAmount: number = 0;
    const facilityInfoBodyList = facilityList.map((facility: any) => {
      premiumSumAmount += facility.amt;

      return [
        {
          children:
            (facility.facilityNo ?? "") + ":" + (facility.facilityName ?? ""),
        },
        {
          children: facility.facilityKind,
        },
        {
          children: facility.facilityKbn,
        },
        {
          children: facility.premiumPaymentStaffNum ?? facility.premiumStaffNum,
        },
        {
          children: facility.unitPremiumStaffNum,
        },
        {
          children: facility.triplePremiumStaffNum,
        },
        {
          children: changeAmountFormat(facility.amt),
        },
      ];
    });

    setPremiumSum(premiumSumAmount);
    return facilityInfoBodyList;
  }

  // 機構へ提出 && 申請
  const handleSubmit = async (date?: any) => {
    try {
      // 掛金納付対象職員届更新API呼び出し
      const submitDataListReq = {
        ...submitDataList,
        receptionDate: date,
      };
      const res = await httpAPI_PRE006(submitDataListReq);

      if (res.resultCd === "I0000") {
        setState({
          ...state,
          errorMsgId: "",
        });
        // API呼び出しの結果、正常終了した場合 提出完了画面に遷移
        navigate("/SCRC070008");
      } else {
        setState({
          ...state,
          errorMsgId:
            (res as any).resultMsg || (res as any).message || res.resultCd,
        });
      }
      setIsOpen(false);
    } catch (error) {
      navigate("/error");
    }
  };

  return (
    <Box className="SCRC080006" data-testid="SCRC080006PageForTest">
      <TkStepper steps={stepperData} activeStep={activeStep} />
      {new Date().getMonth() < 3 && (
        <Box className="explain" data-testid="SCRC080006Explain">
          <Box>
            　掛金納付対象職員届での登録内容と4月1日付で登録した施設・事業に基づき、
            <span>納付する金額は次のとおり</span>です。
          </Box>
          <Box>
            　誤りがないことを確認し、「機構へ提出する」ボタンを押してください。
          </Box>
          <Box>
            　なお、<span>掛金の振込依頼書</span>
            は、「機構へ提出する」ボタンをクリックした後、翌朝までに共済契約者の
            <span>ホーム画面にダウンロードするためのリンクが表示されます</span>
            ので当該リンクからダウンロードしてください。
          </Box>
        </Box>
      )}
      {/* ユーザタイプが機構の場合 */}
      {isKiko && (
        <TkContractorAndNumberLine
          name="契約者（法人）名"
          no={`${contractorNo}:${businessState.common?.contractorName}`}
        />
      )}
      <Box className="totalPremium" data-testid="SCRC080006PremiumSum">
        納付する掛金合計額：{changeAmountFormat(premiumSum)}円
      </Box>
      <Box>{labelids.labelid.LBLC080006_02}</Box>
      <TkTable
        headList={tableHeadList}
        bodyList={premiumFacilityBodyList}
        showUnit={true}
      ></TkTable>
      <Box className="buttonList">
        <TkBackButton></TkBackButton>
        {isBtnVisible && (
          <TkButton data-testid="submitButton" onClick={() => setIsOpen(true)}>
            {isKiko ? "申請" : "機構へ提出する"}
          </TkButton>
        )}
      </Box>

      {isKiko ? (
        <TkDateDialog
          open={isOpen}
          onOky={handleSubmit}
          onCancel={() => setIsOpen(false)}
          minDate={new Date("1900/01/01")}
          maxDate={new Date()}
          title="確認（申請）"
          contextText="　申請を行いますが、問題ないでしょうか。"
          cancelText="いいえ"
          okText="はい"
        />
      ) : (
        <TkMessageDialog
          data-testid="TkMessageDialog"
          open={isOpen}
          onOky={handleSubmit}
          onCancel={() => setIsOpen(false)}
          title="確認（機構へ提出する）"
          contextText="　機構へ提出を行いますが、問題ないでしょうか。"
          cancelText="いいえ"
          okText="はい"
        />
      )}
    </Box>
  );
};

export default SCRC080006;
